import { useState } from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Form, Radio, Upload, Card, Image, Space, Button as AntButton, Input } from 'antd';
import DownloadOutlined from '@mui/icons-material/Download';
import DeleteForever from '@mui/icons-material/DeleteForever';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Swal from 'sweetalert2';
import InputForm from '../../InputT';
import SearchModal from '../../SearchModal';
import type ParamsPicklist from '../../../Types/ParamsPicklist';
import { IMAGE_MAX_MO_KYC } from '../../../Models/DemandeKyc';
import { LONGUEUR_MIN_DATA_URI } from './KycImageThumb';

interface EditableValueFieldProps {
  label: string;
  name: string;
  ancienneValeur: string;
  type?: 'texte' | 'date' | 'select' | 'radio' | 'image' | 'document';
  editable?: boolean;
  // Requis pour type='select' (réutilise SearchModal, donc a besoin de
  // l'instance de formulaire), type='radio' (Form.Item explicite) et
  // type='image'/'document' (on pousse la data URI dans le formulaire
  // nous-mêmes).
  form?: any;
  picklist?: ParamsPicklist;
  radioOptions?: { value1: string; libelle1: string; value2: string; libelle2: string };
  // Pour type='select' : libellé humain du code déjà en base (ex. CLARIS='B'
  // → "ASSEZ BON RISQUE"), un code seul ne veut rien dire pour l'utilisateur.
  ancienneLibelle?: string;
}

function readFileAsDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
  });
}

// Un seul champ, directement modifiable : la valeur actuelle s'affiche en
// repère au-dessus, l'utilisateur tape/choisit la nouvelle valeur juste en
// dessous — pas de paire "ancienne/nouvelle" côte à côte, pas de sélecteur de
// type. Tant que editable=false (avant clic sur "Modifier"), affiche juste la
// valeur actuelle en lecture seule — pas de doublon avec une carte "fiche".
export default function EditableValueField({
  label, name, ancienneValeur, type = 'texte', editable = true, form, picklist, radioOptions, ancienneLibelle,
}: EditableValueFieldProps) {
  const [previewDataUri, setPreviewDataUri] = useState<string | undefined>(undefined);
  const [previewOuvert, setPreviewOuvert] = useState(false);
  const ancienneValeurAffichee = ancienneValeur && ancienneLibelle ? `${ancienneValeur} — ${ancienneLibelle}` : ancienneValeur;

  if (type === 'image' || type === 'document') {
    // Même composant (Card + Upload + Image) que l'écran CLIENT PHYSIQUE
    // (ClientPysique/Tab3.tsx, onglet "Photo et Signature") — pour rester
    // uniforme avec le reste de l'appli plutôt qu'un aperçu maison à part.
    // type='document' (ex. pièces jointes d'une procuration) accepte en plus
    // les PDF/Word — une data URI non-image (ex. "data:application/pdf;...")
    // ne peut pas passer par <Image>, donc un simple lien "Voir le document".
    const apercu = previewDataUri ?? ancienneValeur;
    // Même seuil que KycImageThumb : une fiche de test peut porter une data
    // URI tronquée (ex. juste "data:") — tenter de la charger affiche l'icône
    // "image cassée" native du navigateur au lieu du message "Aucune ...
    // enregistrée" attendu. Un upload frais (previewDataUri) est toujours une
    // vraie data URI, donc toujours bien au-dessus du seuil.
    const apercuValide = Boolean(apercu && apercu.length >= LONGUEUR_MIN_DATA_URI);
    const mime = apercuValide ? apercu!.match(/^data:([^;]+);/)?.[1] : undefined;
    const estImage = type === 'image' || (mime ? mime.startsWith('image/') : true);
    const estPdf = mime ? /pdf/i.test(mime) : false;
    return (
      <Box sx={{ mb: 1.5 }}>
        <Card
          title={label}
          hoverable
          cover={
            !apercuValide ? (
              <Box sx={{ height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', fontSize: 13 }}>
                Aucune {label.toLowerCase()} enregistrée
              </Box>
            ) : estImage ? (
              <Image alt={label} src={apercu} style={{ maxHeight: 300, objectFit: 'contain' }} />
            ) : (
              <Box sx={{ height: 150, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <InsertDriveFileOutlined sx={{ fontSize: 40, color: 'text.secondary' }} />
                <AntButton size="small" icon={<VisibilityIcon style={{ fontSize: 14 }} />} onClick={() => setPreviewOuvert(true)}>
                  Voir le document
                </AntButton>
              </Box>
            )
          }
          extra={editable ? (
            <Space>
              <Form.Item name={name} noStyle>
                <Input type="hidden" />
              </Form.Item>
              <Upload
                accept={type === 'document' ? 'image/*,.pdf,application/pdf,.doc,.docx' : 'image/*'}
                showUploadList={false} maxCount={1}
                beforeUpload={(file) => {
                  if (file.size / (1024 * 1024) > IMAGE_MAX_MO_KYC) {
                    Swal.fire({ title: 'Fichier trop volumineux', text: `La taille maximale est de ${IMAGE_MAX_MO_KYC} Mo.`, icon: 'warning' });
                    return Upload.LIST_IGNORE;
                  }
                  readFileAsDataUri(file).then((dataUri) => {
                    setPreviewDataUri(dataUri);
                    form?.setFieldValue(name, dataUri);
                  });
                  return false;
                }}
              >
                <AntButton type="dashed" icon={<DownloadOutlined />}>Upload {label}</AntButton>
              </Upload>
              <AntButton
                type="dashed" disabled={!apercu} icon={<DeleteForever color="error" />}
                onClick={() => { setPreviewDataUri(''); form?.setFieldValue(name, ''); }}
              />
            </Space>
          ) : undefined}
        />

        {/* Même pattern que KycDocumentsPanel (onglet Documents côté
            validation) : une modale avec aperçu + bouton Télécharger, pas un
            lien qui force le téléchargement direct. */}
        <Dialog open={previewOuvert} onClose={() => setPreviewOuvert(false)} maxWidth="md" fullWidth>
          <DialogTitle>{label}</DialogTitle>
          <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', bgcolor: 'grey.100', minHeight: 300 }}>
            {estPdf ? (
              <Box component="iframe" src={apercu} title={label} sx={{ width: '100%', height: '70vh', border: 'none' }} />
            ) : (
              <Typography sx={{ alignSelf: 'center', textAlign: 'center' }} color="text.secondary">
                Aperçu non disponible pour ce type de fichier — téléchargez-le pour l'ouvrir.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <AntButton type="primary" icon={<DownloadOutlined />} href={apercu} download={label} className="bt_add" style={{ color: '#fff' }}>
              Télécharger
            </AntButton>
            <AntButton onClick={() => setPreviewOuvert(false)}>Fermer</AntButton>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  if (!editable) {
    return (
      <Box sx={{ mb: 1.5, p: 1.25, borderRadius: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.3, fontSize: 11 }}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={600}>{ancienneValeurAffichee || '—'}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label} — <Typography component="span" variant="caption" fontWeight={700} color="text.primary">
          {ancienneValeurAffichee || 'non renseigné'}
        </Typography>
      </Typography>

      {type === 'select' && picklist && form ? (
        <SearchModal
          Titre={`Recherche — ${label}`} label="" name={name} width={0} Taille1="45%" Taille2="55%"
          data={[]} Parametre={picklist} notRequired form={form}
          defaultCode={ancienneValeur || undefined}
          defaultLibelle={ancienneLibelle || undefined}
        />
      ) : type === 'radio' && radioOptions ? (
        <Form.Item name={name} noStyle>
          <Radio.Group>
            <Radio value={radioOptions.value1}>{radioOptions.libelle1}</Radio>
            <Radio value={radioOptions.value2}>{radioOptions.libelle2}</Radio>
          </Radio.Group>
        </Form.Item>
      ) : (
        <InputForm
          notRequired width={0} label="" type={type} name={name}
          defaultValue={ancienneValeur || undefined}
          placeholder={`Nouvelle valeur — ${label.toLowerCase()}`}
        />
      )}
    </Box>
  );
}
